<template>
  <div class="space-y-1">
    <label class="block text-sm font-medium text-gray-700">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    
    <!-- Champ avec validation dynamique -->
    <div class="relative">
      <input 
        type="text" 
        :placeholder="placeholder" 
        v-model="inputValue" 
        @input="handleInput"
        @blur="validateInput"
        :maxlength="255"
        class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
        :class="hasError || (!inputValue && required) ? 'border-red-500 bg-red-50' : isValid && inputValue ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'"
      />
      
      <!-- Message d'erreur -->
      <p v-if="hasError" class="text-sm text-red-500 flex items-center">
        <i class="fas fa-exclamation-circle mr-2"></i>
        {{ errorMessage }}
      </p>
      
      <!-- Message si vide et requis -->
      <p v-else-if="!inputValue && required" class="text-sm text-red-500 mt-2 flex items-center">
        <i class="fas fa-exclamation-circle mr-2"></i>
        Titulaire du compte obligatoire
      </p>
      
      <!-- Message de succès -->
      <p v-else-if="isValid && inputValue" class="text-sm text-green-600 mt-2 flex items-center">
        <i class="fas fa-check-circle mr-2"></i>
        Titulaire du compte valide
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'

interface Props {
  label?: string
  required?: boolean
  placeholder?: string
  modelValue: string
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Titulaire du compte',
  required: false,
  placeholder: 'Nom du titulaire du compte',
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'validation-changed', isValid: boolean): void
}>()

// État local
const inputValue = ref(props.modelValue)
const hasError = ref(false)
const isValid = ref(false)
const errorMessage = ref('')

// Validation de l'input
const validateInput = () => {
  const value = inputValue.value.trim()
  
  if (!value) {
    hasError.value = false
    isValid.value = false
    // Pour les champs requis, un champ vide n'est pas valide
    emit('validation-changed', false)
    return
  }
  
  // Validation : au moins 2 caractères
  if (value.length < 2) {
    hasError.value = true
    isValid.value = false
    errorMessage.value = 'Le titulaire du compte doit contenir au moins 2 caractères'
    emit('validation-changed', false)
    return
  }
  
  // Validation : pas plus de 255 caractères
  if (value.length > 255) {
    hasError.value = true
    isValid.value = false
    errorMessage.value = 'Le titulaire du compte ne peut pas dépasser 255 caractères'
    emit('validation-changed', false)
    return
  }
  
  // Validation réussie
  hasError.value = false
  isValid.value = true
  emit('validation-changed', true)
}

// Gestion de la saisie
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  inputValue.value = target.value
  // Déclenche la validation
  validateInput()
}

// Synchronisation avec le v-model
watch(inputValue, (newValue) => {
  emit('update:modelValue', newValue)
})

// Synchronisation depuis le v-model externe
watch(() => props.modelValue, (newValue) => {
  inputValue.value = newValue
  // Toujours déclencher la validation, même si le champ est vide
  validateInput()
}, { immediate: true })

// Déclencher la validation au montage du composant
onMounted(() => {
  validateInput()
})
</script>
